/**
 * Validate form fields and return missing field names
 * @param {Object} formData - Form data object
 * @param {Object} fieldLabels - Object mapping field names to their display labels
 * @param {Array} excludeFields - Array of field names to exclude from validation
 * @returns {Array} - Array of missing field labels
 */
export const validateRequiredFields = (formData, fieldLabels, excludeFields = []) => {
  const missingFields = [];

  Object.keys(fieldLabels).forEach(field => {
    if (!excludeFields.includes(field) && !formData[field]) {
      missingFields.push(fieldLabels[field]);
    }
  });

  return missingFields;
};

/**
 * Validate that end date is not before start date
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {boolean} - true if valid, false otherwise
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return true; // Skip validation if dates are not provided
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  return end >= start;
};

/**
 * Validate offline payment form data
 * @param {Object} formData - Form data to validate
 * @param {Object} strings - Localization strings object
 * @param {boolean} isAmountDisabled - Whether amount field is disabled
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateOfflinePaymentForm = (formData, strings, isAmountDisabled = false) => {
  const errors = [];

  // Define field labels using localization strings
  const fieldLabels = {
    organisationId: strings.org_name,
    planId: strings.plan,
    amount: strings.amount,
    paymentMethod: strings.payment_method,
    startDate: strings.start_date,
    endDate: strings.end_date,
  };

  // Exclude amount field if disabled
  const excludeFields = isAmountDisabled ? ["amount"] : [];

  // Check missing fields
  const missingFields = validateRequiredFields(formData, fieldLabels, excludeFields);
  if (missingFields.length > 0) {
    errors.push({
      type: "missing_fields",
      message: `Please fill the following fields: ${missingFields.join(", ")}`
    });
  }

  // Validate date range
  if (formData.startDate && formData.endDate && !validateDateRange(formData.startDate, formData.endDate)) {
    errors.push({
      type: "invalid_date_range",
      message: "End date cannot be before start date. Please select a valid date range."
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
